import React from "react";
import { LinearProgress, Step, StepLabel, Stepper } from "@mui/material";
import { useTranslation } from "react-i18next";

const QuizGameStepper = ({
  currentStep,
  steps,
  quizTitle,
  pointsEarned,
  totalPoints,
  timeLeft,
  timeLimit,
}) => {
  const getProgressPercentage = () => {
    if (timeLimit <= 0) return 0;
    if (timeLeft >= timeLimit) return 0;
    if (timeLeft <= 0) return 100;
    return ((timeLimit - timeLeft) / timeLimit) * 100;
  };

  const { t } = useTranslation();

  const progressPercentage = getProgressPercentage();

  const getProgressColor = () => {
    if (timeLeft <= 0)
      return "linear-gradient(150deg, #EF4444 12.89%, #F87171 85.22%)";

    const remainingPercentage = (timeLeft / timeLimit) * 100;
    if (remainingPercentage > 50) {
      return "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)";
    } else if (remainingPercentage > 25) {
      return "linear-gradient(150deg, #F59E0B 12.89%, #FCD34D 85.22%)";
    } else {
      return "linear-gradient(150deg, #EF4444 12.89%, #F87171 85.22%)";
    }
  };

  return (
    <div className="border border-[#64DDFF] p-6">
      <div className="xl:flex xl:justify-between xl:items-center">
        <h3 className="text-[18px] font-[Lato] font-[600]">{quizTitle}</h3>
        
        <div className="flex items-center justify-between xl:justify-end gap-2">
          <h3 className="text-[16px] font-[Lato] font-[400] text-[#757575]">
          {t("quizGame.Question")}: {currentStep + 1}/{steps?.length}
        </h3>
          <button className="cursor-default bg-[#64DDFF] py-2 px-4 text-white rounded-full">
            {t("quizGame.Points Earned")}: {pointsEarned}/{totalPoints}
          </button>
        </div>
      </div>

      <LinearProgress
        variant="determinate"
        value={(currentStep / (steps.length - 1)) * 100}
        sx={{ height: 10, borderRadius: 5, display: "none" }}
      />

      <Stepper activeStep={currentStep} alternativeLabel className="my-4">
        {steps.map((step, index) => (
          <Step key={index} completed={index < currentStep}>
            <StepLabel
              className="labelStep"
              slotProps={{
                stepIcon: {
                  sx: {
                    fontSize: {
                      xs: "18px",
                      md: "40px",
                    },
                    fontWeight: 700,
                  },
                },
              }}
              sx={{
                "& .MuiStepLabel-label": {
                  fontSize: {
                    xs: "0px",
                    sm: "12px",
                    md: "14px",
                  },
                  display: {
                    xs: "none",
                    sm: "block",
                  },
                  fontWeight: "400",
                },
              }}
            >
              {/* {step.title} */}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Timer Progress Bar */}
      {timeLimit > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              {t("quizGame.Timer Progress")}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm">⏱️</span>
              <span className="text-sm font-medium">
                {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:
                {String(timeLeft % 60).padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* Progress Bar Container - Fixed initial state */}
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden h-[34px]">
            <div
              className="h-full transition-all duration-300 ease-out rounded-full"
              style={{
                width: `${Math.max(0, Math.min(100, progressPercentage))}%`,
                background: getProgressColor(),
                // Fix for initial state - hide progress bar when time hasn't started
                opacity: timeLeft >= timeLimit ? 0 : 1,
                transform: progressPercentage === 0 ? "scaleX(0)" : "scaleX(1)",
                transformOrigin: "left center",
                // Smooth transition for opacity changes
                transition: "width 300ms ease-out, opacity 300ms ease-out",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizGameStepper;
